import type { ActivitySlim, AdminActivitySlim, ProspectActivitySlim } from './activityStats';
import type { DateRange } from './dashboardRange';
import type { LeadFollowup } from './leadFollowups';
import type { PipelineSummary } from './leadPipeline';
import { isDueToday, progressOf } from './leadLoop';
import type { RunningRound } from './leadRounds';

/**
 * The actions funnel, as arithmetic rather than as a component.
 *
 * ── The question it answers ────────────────────────────────────────────────
 *
 * Kyu: "One of the things I still have a challenge with is knowing what the
 * team is doing, especially for sales." And, on why the per-person split is
 * here at all: "If Alex gets in a micromanaging mode, what isn't Nez doing
 * right now? You can just look at the actions pipeline."
 *
 * Every screen in the Hub before this was backward-looking. `lead_rounds`
 * already holds a forward commitment — a touch target inside a window — and
 * `leadLoop.ts` already works out per lead whether today is owed. It was never
 * added up anywhere, so "how many are we planning to send" and "did we miss
 * anything" had nowhere to be answered.
 *
 * ── Planned is TASKS AND ROUNDS, never a scheduled email ───────────────────
 *
 * Kyu's correction, and it is easy to get wrong:
 *
 *   "Not just 'email scheduled' but Task scheduled. In the Hub I don't think
 *    we have scheduled emails function, but we use Task to remind us when to
 *    send email (or any actions needed) on a lead."
 *
 * There is no scheduled-email feature in the Hub. If this module ever grows
 * one, something has been misunderstood.
 *
 * A CORRECTION TO THE DESIGN NOTE, recorded because the note is wrong and
 * somebody will read it: it names `sales_tasks.due_on, assigned_to`. The live
 * table is `tasks` — lead follow-ups are ordinary task rows so they reach My
 * Tasks, take an assignee and carry comments — read here through
 * `LeadFollowup`. `sales_tasks` is the older table the restructure is retiring.
 *
 * ── Planned is a NOW-number and does not take the window ───────────────────
 *
 * The rest of the page windows on when a lead ARRIVED. Planned is forward
 * work — open tasks and touches still owed — so there is no past window that
 * could narrow it without making it mean nothing. Its ⓘ says so, which is the
 * cheaper fix than a second control; the page just deleted one of those.
 *
 * ── The account-management proxy ───────────────────────────────────────────
 *
 * Kyu raised this himself and it would otherwise quietly corrupt Done:
 *
 *   "Not all emails are sales emails. Some of them are account management and
 *    following up with production process."
 *
 * There is no tag for it and this does not propose one — a new tag is a new
 * thing for three people to remember and it will rot. The free proxy: a touch
 * on a lead that is WON or CLOSED is account management; a touch on a lead
 * still moving is sales. Done counts the second kind, and the page says on
 * screen that it is a proxy rather than pretending the split is exact.
 *
 * Lost is deliberately NOT in that set. A lost deal is not an account being
 * managed; it is a lead that ended, and touches on it are vanishingly rare.
 * Adding it would quietly hide real outreach.
 */

/** The touch kinds. A reply is not a touch — it is what a touch earns. */
const TOUCH_KINDS = new Set(['email_sent', 'call', 'text', 'linkedin', 'meeting']);

/**
 * What counts as work on a PROSPECT (drop 3 of the prospecting note).
 *
 * Kyu: "prospects don't wait on anyone, you have to chase them, and that
 * means certain actions need to be done. That's why we record those
 * efforts." The chase is the connection request (`linkedin_added`) and the
 * message (`linkedin`); a call or a meeting on a prospect would count too. A
 * note — "left the company" — is a finding, not a chase, and is left out for
 * the same reason a note on a lead is.
 *
 * `linkedin_added` is work here and NOT a touch anywhere else: touch feeds
 * the round arithmetic, and a prospect is in no round.
 */
const PROSPECT_WORK_KINDS = new Set([...TOUCH_KINDS, 'linkedin_added']);

export type ActionKey = 'planned' | 'done' | 'replies' | 'admin';

export const ACTION_ORDER: readonly ActionKey[] = ['planned', 'done', 'replies', 'admin'];

export const ACTION_LABELS: Record<ActionKey, string> = {
  planned: 'Planned',
  done: 'Done',
  replies: 'Replies',
  admin: 'Admin',
};

export const ACTION_EXPLAINERS: Record<ActionKey, string> = {
  planned:
    'One row per thing somebody has committed to: an open task with a due date, or a running round. The Hub has no scheduled emails, so this is tasks and rounds. Click it to see them. It is a now-number and the window above does not narrow it — there is no past window that could.',
  done: 'Touches logged in the window — emails, calls, LinkedIn messages and meetings — plus the chase on prospects: connection requests and messages. Replies are not touches. Leads that are Won or Closed are left out as account management, which is a proxy rather than an exact split.',
  replies:
    'Replies received in the window, on leads in this slice. The same reply the Outcome funnel counts, read by date instead of by lead.',
  admin:
    'Deals marked "Updated HubSpot" in the window. Kept on its own line and never added to Done: ticking a box has no natural check the way a reply checks a touch, so it must not be able to inflate the number that looks like selling.',
};

export const ATTENTION_EXPLAINERS = {
  dueToday:
    'A task due today, or a round that owes a touch today to still hit its target. Not done yet.',
  overdue:
    'A task past its due date and still open. A round falling behind its pace is not counted here — it is behind, not missed, and it has days left to catch up.',
  whoDidWhat:
    'What each person logged, planned and has running late. Done includes the chase on prospects, and Prospecting shows that part on its own. It reflects activity that was recorded; it is not a target and there is nothing to hit. Touches with no person on them — the mail sync, an import — count in the totals above and belong to no row here.',
} as const;

/**
 * One thing somebody has committed to doing.
 *
 * Kyu, 7 September, on the first version: "14 'open'? yet when I clicked it,
 * only 4 leads appear." The count was tasks PLUS every touch a running round
 * still owed, and the panel listed leads — so 14 read as 14 leads and the list
 * looked broken. It was not wrong; it was two different units printed as one.
 *
 * So Planned counts COMMITMENTS now — one per open task, one per running round
 * — and the panel lists these items rather than the leads behind them. The
 * count equals the rows, which is what makes the number unmistakable. How many
 * touches a round still owes is detail inside its own row, where it reads as
 * scope rather than as a lead count.
 */
export interface PlannedItem {
  /** Stable across renders. Task id, or the round's id. */
  key: string;
  leadId: string;
  kind: 'task' | 'round';
  /** What it is, in the words the row shows. */
  what: string;
  /** The day it is due, or the day the round's window closes. */
  dueOn: string | null;
  /** Who owns it, or '' — a round belongs to the lead, not to a person. */
  owner: string;
  /**
   * Rounds are never 'overdue'. Behind pace is not missed: the window is still
   * open and there are days left to catch up. See the overdue explainer.
   */
  state: 'overdue' | 'today' | 'ahead';
}

export interface ActionLine {
  key: ActionKey;
  label: string;
  count: number;
  /** The small right-hand word: what the number is made of. */
  unit: string;
  /** The distinct leads behind it, so a click has a list to open. */
  leadIds: string[];
  /**
   * The part of the count that happened on prospects, and how many prospects.
   * Prospects are not leads, so they cannot be rows in the panel; the note
   * says the number instead so the count and the list never look like they
   * disagree — the "14 open, 4 leads" lesson, applied before it repeats.
   */
  prospecting: { count: number; prospectIds: string[] };
  /** Admin only: the deals behind the count, for the note. */
  dealIds?: string[];
}

export interface PersonRow {
  personId: string;
  name: string;
  /** Touches on leads plus the chase on prospects. */
  done: number;
  /** The prospect part of `done`, on its own so the chase is visible. */
  prospecting: number;
  planned: number;
  overdue: number;
  /** "Updated HubSpot" ticks. Never inside `done`. */
  admin: number;
}

export interface ActionsSummary {
  lines: ActionLine[];
  /** The things behind Planned, so the panel can list them. */
  planned: PlannedItem[];
  dueToday: { count: number; leadIds: string[] };
  overdue: { count: number; leadIds: string[] };
  people: PersonRow[];
}

/** The day part of a timestamp, which is what the bounds are expressed in. */
function dayOf(timestamp: string): string {
  return timestamp.slice(0, 10);
}

function inRange(day: string, bounds: DateRange | null): boolean {
  return bounds === null || (day >= bounds.from && day <= bounds.to);
}

/**
 * Leads whose touches read as account management rather than as sales.
 *
 * Won and Closed only. See the note above for why Lost is not here.
 */
function settledLeadIds(summary: PipelineSummary): Set<string> {
  const settled = new Set<string>();
  for (const band of summary.bands) {
    if (band.key !== 'won' && band.key !== 'closed') continue;
    for (const row of band.rows) settled.add(row.leadId);
  }
  return settled;
}

/** Touches still owed inside a running round, floored at zero. */
export function touchesOwed(round: RunningRound): number {
  return Math.max(0, round.touchTarget - round.touchesDone);
}

export function buildActions(input: {
  /** The slice, already windowed by arrival. */
  summary: PipelineSummary;
  activities: readonly ActivitySlim[];
  /**
   * Activity on prospects, which belong to no slice: the slice is leads
   * windowed by arrival, and a prospect has not arrived. So every prospect
   * row in the window counts, whatever slice is chosen.
   */
  prospecting?: readonly ProspectActivitySlim[];
  /** "Updated HubSpot" ticks on deals. Its own line, never in Done. */
  admin?: readonly AdminActivitySlim[];
  followups: readonly LeadFollowup[];
  running: ReadonlyMap<string, RunningRound>;
  bounds: DateRange | null;
  today: string;
  /** id → display name, for the person rows. */
  peopleNames: ReadonlyMap<string, string>;
}): ActionsSummary {
  const { summary, activities, followups, running, bounds, today, peopleNames } = input;
  const prospecting = input.prospecting ?? [];
  const admin = input.admin ?? [];

  const sliceIds = new Set<string>();
  for (const band of summary.bands) {
    for (const row of band.rows) sliceIds.add(row.leadId);
  }
  const settled = settledLeadIds(summary);

  // ── Planned ────────────────────────────────────────────────────────────
  // One row per commitment: an open dated task, or a running round. Not one
  // per owed touch — see PlannedItem for why the count and the list have to
  // be the same number.
  const openTasks = followups.filter(
    (task) => !task.is_completed && task.due_date !== null && sliceIds.has(task.lead_id),
  );

  const stateOf = (due: string | null, canBeLate: boolean): PlannedItem['state'] => {
    if (due === null) return 'ahead';
    const day = dayOf(due);
    if (day === today) return 'today';
    return day < today && canBeLate ? 'overdue' : 'ahead';
  };

  const planned: PlannedItem[] = openTasks.map((task) => ({
    key: task.id,
    leadId: task.lead_id,
    kind: 'task' as const,
    what: task.title.trim() === '' ? 'Follow up' : task.title,
    dueOn: task.due_date,
    owner: task.assignee_id === null ? '' : (peopleNames.get(task.assignee_id) ?? 'Someone else'),
    state: stateOf(task.due_date, true),
  }));

  for (const [leadId, round] of running) {
    if (!sliceIds.has(leadId)) continue;
    const owed = touchesOwed(round);
    if (owed === 0) continue;
    planned.push({
      key: round.roundId,
      leadId,
      kind: 'round',
      // The scope is in the row, where it reads as how much work rather than
      // as how many leads.
      what: `Round ${String(round.number)} · ${String(owed)} ${owed === 1 ? 'touch' : 'touches'} left`,
      dueOn: round.windowEnd,
      owner: '',
      // canBeLate false: a round past its window is waiting on the sweep to
      // close it, not a person who missed something.
      state: isDueToday(progressOf(round, today)) ? 'today' : stateOf(round.windowEnd, false),
    });
  }

  // Soonest first, undated last, so the top of the list is the top of the day.
  planned.sort((a, b) => (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999'));

  // ── Done ───────────────────────────────────────────────────────────────
  const doneRows = activities.filter(
    (row) =>
      TOUCH_KINDS.has(row.kind) &&
      sliceIds.has(row.lead_id) &&
      !settled.has(row.lead_id) &&
      inRange(dayOf(row.occurred_at), bounds),
  );

  // The chase. Not sliced (see the parameter), windowed like Done.
  const chaseRows = prospecting.filter(
    (row) => PROSPECT_WORK_KINDS.has(row.kind) && inRange(dayOf(row.occurred_at), bounds),
  );

  // Admin work. Windowed, not sliced: a deal is not a lead and the slice
  // cannot contain it. The panel lists the deals' contacts where there is
  // one; the note carries the deal count.
  const adminRows = admin.filter((row) => inRange(dayOf(row.occurred_at), bounds));

  // ── Replies ────────────────────────────────────────────────────────────
  // Settled leads are NOT excluded here. A reply is not work somebody chose to
  // do, so the account-management proxy does not apply to it, and dropping a
  // reply from a won lead would make Replies disagree with the Outcome funnel
  // for no reason a reader could guess.
  const replyRows = activities.filter(
    (row) =>
      row.kind === 'reply_received' &&
      sliceIds.has(row.lead_id) &&
      inRange(dayOf(row.occurred_at), bounds),
  );

  const lines: ActionLine[] = [
    {
      key: 'planned',
      label: ACTION_LABELS.planned,
      // Equal to planned.length by construction. The two disagreeing is the
      // exact bug this shape exists to make impossible.
      count: planned.length,
      unit: planned.length === 1 ? 'action' : 'actions',
      leadIds: [...new Set(planned.map((item) => item.leadId))],
      prospecting: { count: 0, prospectIds: [] },
    },
    {
      key: 'done',
      label: ACTION_LABELS.done,
      count: doneRows.length + chaseRows.length,
      unit: 'touches',
      leadIds: [...new Set(doneRows.map((row) => row.lead_id))],
      prospecting: {
        count: chaseRows.length,
        prospectIds: [...new Set(chaseRows.map((row) => row.prospect_id))],
      },
    },
    {
      key: 'replies',
      label: ACTION_LABELS.replies,
      count: replyRows.length,
      unit: 'replies',
      leadIds: [...new Set(replyRows.map((row) => row.lead_id))],
      prospecting: { count: 0, prospectIds: [] },
    },
    {
      key: 'admin',
      label: ACTION_LABELS.admin,
      count: adminRows.length,
      unit: 'updates',
      leadIds: [
        ...new Set(adminRows.flatMap((row) => (row.lead_id === null ? [] : [row.lead_id]))),
      ],
      prospecting: { count: 0, prospectIds: [] },
      dealIds: [...new Set(adminRows.map((row) => row.deal_id))],
    },
  ];

  // ── Due today and Overdue ──────────────────────────────────────────────
  // Read off the same list the panel shows, so a chip can never disagree with
  // the rows it opens.
  const dueTodayLeads = new Set(
    planned.filter((item) => item.state === 'today').map((item) => item.leadId),
  );
  const overdueLeads = new Set(
    planned.filter((item) => item.state === 'overdue').map((item) => item.leadId),
  );

  // ── Who did what ───────────────────────────────────────────────────────
  const rows = new Map<string, PersonRow>();
  const touch = (personId: string): PersonRow => {
    const held = rows.get(personId);
    if (held !== undefined) return held;
    const made: PersonRow = {
      personId,
      name: peopleNames.get(personId) ?? 'Someone else',
      done: 0,
      prospecting: 0,
      planned: 0,
      overdue: 0,
      admin: 0,
    };
    rows.set(personId, made);
    return made;
  };

  for (const row of doneRows) {
    // Null actor is ordinary — the mail sync writes rows nobody clicked for.
    // It counts in Done above and belongs to no person here.
    if (row.actor_id === null) continue;
    touch(row.actor_id).done += 1;
  }
  for (const row of chaseRows) {
    if (row.actor_id === null) continue;
    const person = touch(row.actor_id);
    person.done += 1;
    person.prospecting += 1;
  }
  for (const row of adminRows) {
    if (row.actor_id === null) continue;
    touch(row.actor_id).admin += 1;
  }
  for (const task of openTasks) {
    if (task.assignee_id === null) continue;
    const person = touch(task.assignee_id);
    person.planned += 1;
    if (task.due_date !== null && dayOf(task.due_date) < today) {
      person.overdue += 1;
    }
  }

  return {
    lines,
    planned,
    dueToday: { count: dueTodayLeads.size, leadIds: [...dueTodayLeads] },
    overdue: { count: overdueLeads.size, leadIds: [...overdueLeads] },
    // Busiest first, then by name so the order is stable when two people tie.
    people: [...rows.values()].sort(
      (a, b) => b.done - a.done || b.planned - a.planned || a.name.localeCompare(b.name),
    ),
  };
}
